import {
  Community,
  Leaderboard,
  League,
  LeaguesApi,
  MatchStatus,
  PageBareMatch,
  PageBareTeamWithCounts,
  PageLeaderboardRow,
  PageTableRow,
  PlayersLeaderboard,
  TableRow,
} from "@virtualprogaming/js-public-sdk";
import { useMemo } from "react";
import { useQuery, UseQueryOptions } from "react-query";

import { SearchRequest } from "~/queries/types";
import { useApiConfig } from "~/utils";

export function useLeagueApi() {
  const config = useApiConfig();
  return useMemo(() => new LeaguesApi(...config), [config]);
}

export function useLeagueTeams(
  slug: string,
  rq: SearchRequest,
  options?: UseQueryOptions<PageBareTeamWithCounts>
) {
  const api = useLeagueApi();
  const { ...pagination } = rq;
  return useQuery({
    queryKey: ["leagues", slug, "teams", pagination],
    queryFn: async () => {
      const { data } = await api.searchTeams(
        slug,
        pagination.limit,
        pagination.offset,
        pagination.query
      );
      return data;
    },
    ...options,
  });
}

export function useLeagueTable(
  slug: string,
  season?: number,
  isHistory?: boolean,
  options?: UseQueryOptions<TableRow[]>
) {
  const api = useLeagueApi();
  return useQuery({
    queryKey: ["leagues", slug, "table", isHistory, { season }],
    queryFn: async () => {
      const { data } = await api.getTable(slug, season, isHistory);
      return data;
    },
    ...options,
  });
}

export function useLeagueTeamsLeaderboardMostGames(
  slug: string,
  options?: UseQueryOptions<PageTableRow>,
  limit?: number,
  offset?: number
) {
  const api = useLeagueApi();
  return useQuery({
    queryKey: ["leagues", slug, "most-played", { limit, offset }],
    queryFn: async () => {
      const { data } = await api.getMostPlayedTeams(slug, limit, offset);
      return data;
    },
    ...options,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: 1 * 60 * 60 * 2000,
    staleTime: 1 * 60 * 60 * 2000,
    retry: 1,
  });
}

export function useLeagueTeamsLeaderboardMostTitles(
  slug: string,
  options?: UseQueryOptions<PageTableRow>,
  limit?: number,
  offset?: number
) {
  const api = useLeagueApi();
  return useQuery({
    queryKey: ["leagues", slug, "most-titles", { limit, offset }],
    queryFn: async () => {
      const { data } = await api.getMostTitlesTeams(slug, limit, offset);
      return data;
    },
    ...options,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: 1 * 60 * 60 * 2000,
    staleTime: 1 * 60 * 60 * 2000,
    retry: 1,
  });
}

export function useLeagueTeamsLeaderboardMostPoints(
  slug: string,
  options?: UseQueryOptions<PageTableRow>,
  limit?: number,
  offset?: number
) {
  const api = useLeagueApi();
  return useQuery({
    queryKey: ["leagues", slug, "most-points", { limit, offset }],
    queryFn: async () => {
      const { data } = await api.getMostPointsTeams(slug, limit, offset);
      return data;
    },
    ...options,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: 1 * 60 * 60 * 2000,
    staleTime: 1 * 60 * 60 * 2000,
    retry: 1,
  });
}

export function useLeagueTeamsLeaderboardMostTitled(
  slug: string,
  options?: UseQueryOptions<PageTableRow>,
  limit?: number,
  offset?: number
) {
  const api = useLeagueApi();
  return useQuery({
    queryKey: ["leagues", slug, "most-titled", { limit, offset }],
    queryFn: async () => {
      const { data } = await api.getMostTitledTeams(slug, limit, offset);
      return data;
    },
    ...options,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: 1 * 60 * 60 * 2000,
    staleTime: 1 * 60 * 60 * 2000,
    retry: 1,
  });
}

export function useLeague(slug: string, options?: UseQueryOptions<League>) {
  const api = useLeagueApi();
  return useQuery({
    queryKey: ["leagues", slug],
    queryFn: async () => {
      const { data } = await api.getLeague(slug);
      return data;
    },
    ...options,
  });
}

export function useLeagueMatchesByStatusPaged(
  slug: string,
  status: MatchStatus,
  season?: number,
  matchDay?: number,
  datetime?: string,
  limit?: number,
  offset?: number,
  options?: UseQueryOptions<PageBareMatch>
) {
  const api = useLeagueApi();
  return useQuery({
    queryKey: [
      "leagues",
      slug,
      "matches",
      datetime,
      matchDay,
      { status, season },
      { limit, offset },
    ],
    queryFn: async () => {
      const { data } = await api.getLeagueMatchesByStatusPaged(
        slug,
        status,
        season,
        matchDay,
        datetime,
        limit,
        offset
      );
      return data;
    },
    ...options,
  });
}

export function useLeagueLeaderboardPaged(
  slug: string,
  leaderboard: Leaderboard,
  season?: number,
  weekly = false,
  limit?: number,
  offset?: number,
  options?: UseQueryOptions<PageLeaderboardRow>
) {
  const api = useLeagueApi();
  return useQuery({
    queryKey: [
      "leagues",
      slug,
      "leaderboard",
      season,
      leaderboard,
      weekly,
      { limit, offset },
    ],
    queryFn: async () => {
      const { data } = await api.getLeagueLeaderboardPaged(
        slug,
        leaderboard,
        weekly,
        season,
        limit,
        offset
      );
      return data;
    },
    ...options,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: 1 * 60 * 60 * 2000,
    staleTime: 1 * 60 * 60 * 2000,
    retry: 1,
  });
}

export function useLeaguePlayersLeaderboardPaged(
  slug: string,
  leaderboard: PlayersLeaderboard,
  limit?: number,
  offset?: number,
  options?: UseQueryOptions<PageLeaderboardRow>
) {
  const api = useLeagueApi();
  return useQuery({
    queryKey: ["leagues", slug, "leaderboard", leaderboard, { limit, offset }],
    queryFn: async () => {
      const { data } = await api.getLeaguePlayersLeaderboardPaged(
        slug,
        leaderboard,
        limit,
        offset
      );
      return data;
    },
    ...options,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: 1 * 60 * 60 * 2000,
    staleTime: 1 * 60 * 60 * 2000,
    retry: 1,
  });
}

export function useLeagueCommunity(
  slug: string,
  options?: UseQueryOptions<Community>
) {
  const api = useLeagueApi();
  return useQuery({
    queryKey: ["leagues", slug, "community"],
    queryFn: async () => {
      const { data } = await api.getLeagueCommunity(slug);
      return data;
    },
    ...options,
  });
}

export function useLeagueSeasons(
  slug: string,
  options?: UseQueryOptions<number[]>
) {
  const api = useLeagueApi();
  return useQuery({
    queryKey: ["leagues", slug, "seasons"],
    queryFn: async () => {
      const { data } = await api.getLeagueSeasons(slug);
      return data;
    },
    ...options,
  });
}
